import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import type { Warehouse } from './types'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from './useColumnPrefs'
import { SortableColHeader, ColumnToggleButton, computeAutoFitWidths } from './ColDragHeader'
import { useStickyHScroll } from '../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../components/StickyHScrollbar'
import { daysBetween, OVERDUE_ROW_CLASS, OVERDUE_TEXT_CLASS, overdueBadgeText } from '../../lib/overdue'

// ─── Types ────────────────────────────────────────────────────────────────────

type WhStatus = 'pre_devanning' | 'devanning_planned' | 'devanning_started' | 'devanning_completed' | 'cancelled'

const WH_STATUS_COLORS: Record<string, string> = {
  pre_devanning:       'bg-gray-100 text-gray-600',
  devanning_planned:   'bg-blue-100 text-blue-700',
  devanning_started:   'bg-yellow-100 text-yellow-700',
  devanning_completed: 'bg-green-100 text-green-700',
  cancelled:           'bg-red-100 text-red-600',
}

const WH_STATUS_LABELS: Record<string, string> = {
  pre_devanning:       'Pre-Devanning',
  devanning_planned:   'Awaiting Devanning',
  devanning_started:   'Devanning Started',
  devanning_completed: 'Devanning Completed',
  cancelled:           'Cancelled',
}

const CUSTOMS_OPTIONS = ['', 'PRE', 'SUBMITTED', 'ACCEPTED', 'CLEARED', 'REJECTED', 'HOLD']

const CUSTOMS_COLORS: Record<string, string> = {
  PRE:       'bg-gray-100 text-gray-600',
  SUBMITTED: 'bg-blue-100 text-blue-700',
  ACCEPTED:  'bg-indigo-100 text-indigo-700',
  CLEARED:   'bg-green-100 text-green-700',
  REJECTED:  'bg-red-100 text-red-600',
  HOLD:      'bg-amber-100 text-amber-700',
}

interface ScheduleRow {
  scheduleId: string        // from devanning_schedule (always present)
  planId: string | null     // from devanning_plans (null = Pre-Devanning, no LL uploaded)
  devanningId: string | null
  warehouseId: string
  warehouseCode: string
  plannedDevanAt: string
  dateStr: string
  containerNo: string
  jobRef: string | null
  customerName: string | null
  warehouseStatus: WhStatus
  customsStatus: string | null
  customsOverride: string | null
  customsOverrideBy: string | null
  customsOverrideAt: string | null
  holdReason: string | null
  sourceLoadingListId: string | null
  eta: string | null
  freeDays: number | null
  freeDate: string | null
  createdAt: string | null
  createdBy: string | null
  lastReason: string | null
  lastChangedAt: string | null
  lastChangedBy: string | null
}

interface WeekGroup {
  weekKey: string
  weekLabel: string
  rows: ScheduleRow[]
}

interface BatchRow {
  _id: string
  planned_date: string
  planned_time: string
  job_ref: string
  warehouse_id: string
  warehouse_code: string
  container_no: string
  error: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function toLocalDateInput(iso: string): string { return toDateStr(new Date(iso)) }
function toLocalTimeInput(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
function toLocalDateTimeInput(iso: string): string {
  return `${toLocalDateInput(iso)}T${toLocalTimeInput(iso)}`
}

function computeFreeDate(eta: string | null, freeDays: number | null): string | null {
  if (!eta || freeDays == null) return null
  const d = new Date(eta)
  d.setDate(d.getDate() + freeDays)
  return toDateStr(d)
}

function fmtShortDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtCreated(at: string | null, by: string | null): string {
  const datePart = at ? new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
  const byPart = by ? by.split('@')[0] : ''
  if (!datePart && !byPart) return '—'
  return [datePart, byPart].filter(Boolean).join(' · ')
}

function getWeekInfo(dateStr: string): { weekKey: string; weekLabel: string } {
  const d = new Date(`${dateStr}T12:00:00`)
  const dayOfWeek = (d.getDay() + 6) % 7
  const monday = new Date(d.getTime() - dayOfWeek * 86400000)
  const sunday = new Date(monday.getTime() + 6 * 86400000)
  const mondayStr = toDateStr(monday)
  const jan1 = new Date(monday.getFullYear(), 0, 1)
  const weekNum = Math.ceil(((monday.getTime() - jan1.getTime()) / 86400000 + (jan1.getDay() + 6) % 7 + 1) / 7)
  return {
    weekKey: mondayStr,
    weekLabel: `Week ${weekNum} — ${monday.getDate()}–${sunday.getDate()} ${sunday.toLocaleDateString('en-GB', { month: 'short' })} ${sunday.getFullYear()}`,
  }
}

function derivedStatus(_planStatus: string, devStatus: string | null): WhStatus {
  if (!devStatus) return 'pre_devanning'
  if (devStatus === 'awaiting')  return 'devanning_planned'
  if (devStatus === 'started')   return 'devanning_started'
  if (devStatus === 'completed') return 'devanning_completed'
  if (devStatus === 'cancelled') return 'cancelled'
  return 'pre_devanning'
}

function newBlankRow(): BatchRow {
  return { _id: String(Date.now() + Math.random()), planned_date: '', planned_time: '', job_ref: '', warehouse_id: '', warehouse_code: '', container_no: '', error: null }
}

function parseDateTimeStr(s: string): { date: string; time: string } | null {
  if (!s.trim()) return null
  let m = s.match(/^(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})[T\s](\d{1,2}):(\d{2})/)
  if (m) return { date: `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`, time: `${m[4].padStart(2,'0')}:${m[5]}` }
  m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})[T\s](\d{1,2}):(\d{2})/)
  if (m) return { date: `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`, time: `${m[4].padStart(2,'0')}:${m[5]}` }
  m = s.match(/^(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})$/)
  if (m) return { date: `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`, time: '' }
  m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/)
  if (m) return { date: `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`, time: '' }
  return null
}

function parsePasteRows(text: string, whs: Warehouse[]): BatchRow[] {
  return text.split('\n').map(l => l.trim()).filter(Boolean).map((line, i) => {
    const cols = line.split('\t')
    const dtStr        = (cols[0] ?? '').trim()
    const timeStr      = (cols[1] ?? '').trim()
    const job_ref      = (cols[2] ?? '').trim().toUpperCase()
    const whCode       = (cols[3] ?? '').trim().toUpperCase()
    const container_no = (cols[4] ?? '').trim().toUpperCase()
    const dt = parseDateTimeStr(dtStr)
    const finalDate = dt?.date ?? ''
    const finalTime = /^\d{1,2}:\d{2}$/.test(timeStr) ? timeStr : (dt?.time ?? '')
    const wh = whs.find(w => w.code.toUpperCase() === whCode)
    const error = !finalDate ? (dtStr ? `Cannot parse date "${dtStr}"` : 'Date missing')
      : !job_ref ? 'Job Ref missing'
      : !wh ? (whCode ? `Unknown warehouse "${whCode}"` : 'Warehouse missing')
      : null
    return { _id: `paste_${i}`, planned_date: finalDate, planned_time: finalTime, job_ref, warehouse_id: wh?.id ?? '', warehouse_code: wh?.code ?? whCode, container_no, error }
  })
}

// ─── Inline components ────────────────────────────────────────────────────────

const cellCls = 'w-full border border-blue-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white'

function Spin() {
  return (
    <svg className="animate-spin w-3 h-3 inline text-blue-500" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

function InlineText({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.select() }, [])
  return <input ref={ref} className={cellCls} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)} onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function InlineDate({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <input ref={ref} type="date" className={cellCls} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)} onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function InlineTime({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <input ref={ref} type="time" className={cellCls} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)} onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function InlineSelect<T extends string>({ value, options, onCommit, onCancel }: {
  value: T; options: { value: T; label: string }[]; onCommit: (v: T) => void; onCancel: () => void
}) {
  const ref = useRef<HTMLSelectElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <select ref={ref} className={cellCls} defaultValue={value}
    onChange={e => onCommit(e.target.value as T)} onBlur={() => onCancel()}
    onKeyDown={e => { if (e.key === 'Escape') onCancel() }}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
}

function InlineCustoms({ value, onCommit, onCancel, onRequestHold }: {
  value: string; onCommit: (v: string) => void; onCancel: () => void; onRequestHold: () => void
}) {
  const ref = useRef<HTMLSelectElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <select ref={ref} className={cellCls} defaultValue={value}
    onChange={e => { if (e.target.value === 'HOLD') { onRequestHold() } else { onCommit(e.target.value) } }}
    onBlur={() => onCancel()}
    onKeyDown={e => { if (e.key === 'Escape') onCancel() }}>
    {CUSTOMS_OPTIONS.map(o => <option key={o} value={o}>{o || '—'}</option>)}
  </select>
}

const editIcon = (
  <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" />
  </svg>
)

function InlineNote({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { ref.current?.focus(); ref.current?.select() }, [])
  return (
    <textarea ref={ref} rows={2} className={`${cellCls} text-xs resize-none min-w-[140px]`} value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(v) } if (e.key === 'Escape') onCancel() }}
    />
  )
}

function InfoIcon({ title }: { title: string }) {
  return (
    <span title={title} className="ml-1 text-gray-400 hover:text-gray-600 cursor-help inline-flex items-center align-middle">
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    </span>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type EditField = 'container_no' | 'warehouse_id' | 'planned_date' | 'planned_time' | 'customs_status' | 'hold_note'

const ALL_SCHED_COL_DEFS = [
  { id: 'date',      label: 'Planned Devan Date' },
  { id: 'job_ref',   label: 'Job Ref' },
  { id: 'customer',  label: 'Customer' },
  { id: 'container', label: 'Container' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'eta',       label: 'ETA' },
  { id: 'free_days', label: 'Free Days' },
  { id: 'free_date', label: 'Free Date' },
  { id: 'time',      label: 'Time' },
  { id: 'wh_status', label: 'WH Status' },
  { id: 'customs',   label: 'Customs Status' },
  { id: 'hold_note', label: 'Customs Notes' },
  { id: 'created',   label: 'Created' },
] as const

type SchedColKey = typeof ALL_SCHED_COL_DEFS[number]['id']

const SCHED_AUTO_WIDTHS = computeAutoFitWidths(ALL_SCHED_COL_DEFS)

const SORTABLE_COLS = new Set<SchedColKey>(['date', 'job_ref', 'customer', 'time', 'warehouse', 'wh_status', 'customs', 'created'])

const WH_STATUS_ORDER: Record<string, number> = {
  pre_devanning: 0, devanning_planned: 1, devanning_started: 2, devanning_completed: 3, cancelled: 4,
}
const CUSTOMS_SORT_ORDER: Record<string, number> = {
  PRE: 0, SUBMITTED: 1, ACCEPTED: 2, CLEARED: 3, REJECTED: 4, HOLD: 5,
}

function getSortKey(row: ScheduleRow, col: SchedColKey): string {
  switch (col) {
    case 'date':     return row.plannedDevanAt ?? row.dateStr
    case 'job_ref':  return (row.jobRef ?? '').toLowerCase()
    case 'customer': return (row.customerName ?? '').toLowerCase()
    case 'time':     return toLocalTimeInput(row.plannedDevanAt)
    case 'warehouse': return row.warehouseCode.toLowerCase()
    case 'wh_status': return String(WH_STATUS_ORDER[row.warehouseStatus] ?? 9).padStart(2, '0')
    case 'customs':  return String(CUSTOMS_SORT_ORDER[row.customsOverride ?? row.customsStatus ?? ''] ?? 9).padStart(2, '0')
    case 'created':  return row.createdAt ?? ''
    default:         return ''
  }
}

export default function SchedulePage() {
  const { userEmail, user } = useAuth()
  const [rows, setRows] = useState<ScheduleRow[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [collapsedWeeks, setCollapsedWeeks] = useState<Set<string>>(new Set())
  const [whFilter, setWhFilter] = useState('all')

  const [editCell, setEditCell] = useState<{ rowId: string; field: EditField; value: string } | null>(null)
  const [savingCell, setSavingCell] = useState<string | null>(null)

  type EntryMode = 'single' | 'multi' | 'paste'
  const [entryDialog, setEntryDialog] = useState(false)
  const [entryMode, setEntryMode] = useState<EntryMode>('single')
  const [singleEntry, setSingleEntry] = useState({ planned_date: '', planned_time: '', job_ref: '', warehouse_id: '', container_no: '' })
  const [multiRows, setMultiRows] = useState<BatchRow[]>([])
  const [pasteText, setPasteText] = useState('')
  const [pasteRows, setPasteRows] = useState<BatchRow[]>([])
  const [savingEntries, setSavingEntries] = useState(false)

  type ActionType = 'edit' | 'cancel'
  const [actionDialog, setActionDialog] = useState<{ type: ActionType; row: ScheduleRow } | null>(null)
  const [editForm, setEditForm] = useState({ jobRef: '', warehouseId: '', plannedDevanAt: '', containerNo: '', reason: '' })
  const [actionReason, setActionReason] = useState('')
  const [actionSaving, setActionSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<ScheduleRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Duplicate job_ref detection
  const singleDupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const editDupTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null)
  type DupPlan = { id: string; created_at: string | null }
  const [singleDupCheck, setSingleDupCheck] = useState<DupPlan | 'checking' | null>(null)
  const [editDupCheck,   setEditDupCheck]   = useState<DupPlan | 'checking' | null>(null)

  const [holdDialog, setHoldDialog] = useState<ScheduleRow | null>(null)
  const [holdReason, setHoldReason] = useState('')
  const [holdSaving, setHoldSaving] = useState(false)

  // No-LL banner + filter
  const [noLLBannerDismissed, setNoLLBannerDismissed] = useState(false)
  const [filterNoLL, setFilterNoLL] = useState(false)

  // Column drag-reorder + show/hide + sort prefs (migrates from old localStorage + 'schedule_cols' DB key)
  const { order: schedColOrder, hidden: schedHidden, extra: schedExtra, visibleCols,
    setOrder: setSchedOrder, toggleHidden: toggleSchedCol, setExtra: setSchedExtra, reset: resetSchedCols } =
    useColumnPrefs(
      'col_pref_schedule', ALL_SCHED_COL_DEFS, user?.id,
      { dbKey: 'schedule_cols', lsHidden: 'sched_hidden_cols', lsOrder: 'sched_col_order' },
      { sortBy: 'date', sortDir: 'asc', widths: {} }
    )
  const schedSavedWidths = (schedExtra.widths as Record<string, number>) ?? {}
  const getSchedColW = (id: string) => schedSavedWidths[id] ?? SCHED_AUTO_WIDTHS[id] ?? 120
  const saveSchedColW = (id: string, w: number) => setSchedExtra({ ...schedExtra, widths: { ...schedSavedWidths, [id]: w } })
  const resetSchedColW = (id: string) => { const n = { ...schedSavedWidths }; delete n[id]; setSchedExtra({ ...schedExtra, widths: n }) }

  const sortCol = (schedExtra.sortBy as SchedColKey | null) ?? 'date'
  const sortDir = (schedExtra.sortDir as 'asc' | 'desc' | null) ?? 'asc'

  const handleColSort = (colId: SchedColKey) => {
    let newDir: 'asc' | 'desc' | null
    if (sortCol !== colId) {
      newDir = 'asc'
    } else {
      newDir = sortDir === 'asc' ? 'desc' : sortDir === 'desc' ? null : 'asc'
    }
    const newCol: SchedColKey | null = newDir === null ? null : colId
    setSchedExtra({ sortBy: newCol, sortDir: newDir })
  }

  const schedSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handleSchedColDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oi = schedColOrder.indexOf(String(active.id))
      const ni = schedColOrder.indexOf(String(over.id))
      if (oi !== -1 && ni !== -1) setSchedOrder(arrayMove(schedColOrder, oi, ni))
    }
  }

  useEffect(() => {
    supabase.from('warehouses').select('*').eq('is_active', true).order('name')
      .then(({ data }) => setWarehouses((data as Warehouse[]) || []))
  }, [])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      // 1. All schedule entries (source of truth for Schedule tab)
      const { data: schedEntries, error: schedErr } = await supabase
        .from('devanning_schedule')
        .select('id, job_ref, warehouse_id, container_no, planned_devan_at, created_at, created_by, last_reason, last_changed_at, last_changed_by')
        .order('planned_devan_at', { ascending: true })
      if (schedErr) { setLoadError(schedErr.message); return }
      if (!schedEntries || schedEntries.length === 0) { setRows([]); return }

      // 2. Plans for matching job_refs (will be null for Pre-Devanning entries)
      // NOTE: LL-linked plans have devanning_plans.job_ref = NULL (job_ref lives on the loading_list).
      // We must also look up by source_loading_list_id to find those plans.
      const jobRefs = (schedEntries as any[]).map(s => s.job_ref).filter(Boolean)
      const planMap: Record<string, any> = {}
      if (jobRefs.length > 0) {
        const PLAN_SELECT = `
          id, container_no, planned_devan_at, warehouse_id, source_loading_list_id, status,
          job_ref, customs_override, customs_override_by, customs_override_at, hold_reason,
          last_reason, last_changed_at, last_changed_by,
          warehouse:warehouses!warehouse_id(id, code),
          ll:loading_lists!source_loading_list_id(
            job_ref, container_eta_at, free_demurrage_days,
            customer:customers!source_customer_id(name)
          ),
          devannings(id, status, last_reason)
        `
        // 2a. Plans with direct job_ref
        const { data: plansA } = await supabase
          .from('devanning_plans')
          .select(PLAN_SELECT)
          .in('job_ref', jobRefs)

        // 2b. Plans linked via loading_list (devanning_plans.job_ref IS NULL)
        const { data: llData } = await supabase
          .from('loading_lists')
          .select('id, job_ref')
          .in('job_ref', jobRefs)
        const llIds = (llData ?? []).map((ll: any) => ll.id as string)
        const { data: plansB } = llIds.length > 0
          ? await supabase.from('devanning_plans').select(PLAN_SELECT).in('source_loading_list_id', llIds).is('job_ref', null)
          : { data: [] }

        for (const p of [...(plansA ?? []), ...(plansB ?? [])] as any[]) {
          const llJobRef = Array.isArray(p.ll) ? p.ll[0]?.job_ref : p.ll?.job_ref
          planMap[p.job_ref ?? llJobRef] = p
        }
      }

      // 3. Job customs statuses
      const jobStatusMap: Record<string, string> = {}
      if (jobRefs.length > 0) {
        const { data: jobs } = await supabase.from('jobs').select('job_ref, status').in('job_ref', jobRefs as string[])
        for (const j of (jobs ?? []) as any[]) jobStatusMap[j.job_ref] = j.status
      }

      // 4. Warehouse code lookup for Pre-Devanning entries (no plan to join)
      const whMap: Record<string, any> = {}
      for (const w of warehouses) whMap[w.id] = w

      // 5. Merge schedule entries with plan data
      const result: ScheduleRow[] = (schedEntries as any[]).map(sched => {
        const plan = planMap[sched.job_ref] ?? null
        const wh = plan
          ? (Array.isArray(plan.warehouse) ? plan.warehouse[0] : plan.warehouse)
          : whMap[sched.warehouse_id]
        const ll = plan ? (Array.isArray(plan.ll) ? plan.ll[0] : plan.ll) : null
        const rawDevs = plan?.devannings
        const devs: any[] = Array.isArray(rawDevs) ? rawDevs : (rawDevs ? [rawDevs] : [])
        const dev = devs.find((d: any) => d.status !== 'cancelled') ?? devs[0] ?? null
        const plannedAt = plan?.planned_devan_at ?? sched.planned_devan_at
        const dateStr = plannedAt?.slice(0, 10) ?? ''
        const eta = ll?.container_eta_at ?? null
        const freeDays = ll?.free_demurrage_days ?? null
        const jobRef = sched.job_ref
        return {
          scheduleId: sched.id,
          planId: plan?.id ?? null,
          devanningId: dev?.id ?? null,
          warehouseId: wh?.id ?? sched.warehouse_id ?? '',
          warehouseCode: wh?.code ?? '?',
          plannedDevanAt: plannedAt ?? sched.planned_devan_at,
          dateStr,
          containerNo: plan?.container_no ?? sched.container_no ?? '',
          jobRef,
          customerName: ll?.customer?.name ?? null,
          warehouseStatus: plan ? derivedStatus(plan.status, dev?.status ?? null) : 'pre_devanning',
          customsStatus: jobRef ? (jobStatusMap[jobRef] ?? null) : null,
          customsOverride: plan?.customs_override ?? null,
          customsOverrideBy: plan?.customs_override_by ?? null,
          customsOverrideAt: plan?.customs_override_at ?? null,
          holdReason: plan?.hold_reason ?? null,
          sourceLoadingListId: plan?.source_loading_list_id ?? null,
          eta,
          freeDays,
          freeDate: computeFreeDate(eta, freeDays),
          createdAt: sched.created_at ?? null,
          createdBy: sched.created_by ?? null,
          lastReason: plan?.last_reason ?? sched.last_reason ?? null,
          lastChangedAt: plan?.last_changed_at ?? sched.last_changed_at ?? null,
          lastChangedBy: plan?.last_changed_by ?? sched.last_changed_by ?? null,
        } as ScheduleRow
      })

      setRows(result)
    } catch (err: any) {
      console.error('Schedule fetchRows error:', err)
      setLoadError(err.message || 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [warehouses])

  useEffect(() => { fetchRows() }, [fetchRows])

  // Clear dup-check timers on unmount
  useEffect(() => () => {
    if (singleDupTimerRef.current) clearTimeout(singleDupTimerRef.current)
    if (editDupTimerRef.current)   clearTimeout(editDupTimerRef.current)
  }, [])

  // Reset editDupCheck whenever the edit dialog closes
  useEffect(() => {
    if (!actionDialog || actionDialog.type !== 'edit') {
      if (editDupTimerRef.current) clearTimeout(editDupTimerRef.current)
      setEditDupCheck(null)
    }
  }, [actionDialog])

  const visibleRows = useMemo(() => {
    let r = whFilter === 'all' ? rows : rows.filter(row => row.warehouseId === whFilter)
    if (filterNoLL) r = r.filter(row => !row.sourceLoadingListId && !!row.jobRef)
    return r
  }, [rows, whFilter, filterNoLL])

  const noLLCount = useMemo(
    () => rows.filter(r => !r.sourceLoadingListId && !!r.jobRef).length,
    [rows]
  )

  const weekGroups = useMemo((): WeekGroup[] => {
    // Build week groups in natural chronological order (DB fetch is already asc by date)
    const map = new Map<string, WeekGroup>()
    for (const row of visibleRows) {
      if (!row.dateStr) continue
      const { weekKey, weekLabel } = getWeekInfo(row.dateStr)
      if (!map.has(weekKey)) map.set(weekKey, { weekKey, weekLabel, rows: [] })
      map.get(weekKey)!.rows.push(row)
    }
    const groups = Array.from(map.values())

    // Sort week group order: date desc = reverse chronological; all other sorts keep weeks asc
    if (sortCol === 'date' && sortDir === 'desc') groups.reverse()

    // Sort rows within each week by active column
    if (sortCol && sortDir) {
      const mul = sortDir === 'asc' ? 1 : -1
      for (const g of groups) {
        g.rows.sort((a, b) => mul * getSortKey(a, sortCol).localeCompare(getSortKey(b, sortCol)))
      }
    }

    return groups
  }, [visibleRows, sortCol, sortDir])

  const { scrollProps: schedScrollProps, contentRef: schedTableRef, bar: schedScrollBar } = useStickyHScroll([weekGroups, visibleCols])

  const toggleWeek = (key: string) => {
    setCollapsedWeeks(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
  }

  const saveCell = useCallback(async (row: ScheduleRow, field: EditField, value: string) => {
    const key = `${row.scheduleId}:${field}`
    setSavingCell(key)
    setEditCell(null)
    try {
      if (field === 'container_no') {
        // Pre-Devanning: update schedule; has-plan: update plan
        if (row.planId) {
          const { error } = await supabase.from('devanning_plans').update({ container_no: value || null }).eq('id', row.planId)
          if (error) throw error
        } else {
          const { error } = await supabase.from('devanning_schedule').update({ container_no: value || null }).eq('id', row.scheduleId)
          if (error) throw error
        }
        setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? { ...r, containerNo: value } : r))

      } else if (field === 'warehouse_id') {
        if (row.planId) {
          const { error } = await supabase.from('devanning_plans').update({ warehouse_id: value }).eq('id', row.planId)
          if (error) throw error
        } else {
          const { error } = await supabase.from('devanning_schedule').update({ warehouse_id: value }).eq('id', row.scheduleId)
          if (error) throw error
        }
        const wh = warehouses.find(w => w.id === value)
        setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? { ...r, warehouseId: value, warehouseCode: wh?.code ?? '?' } : r))

      } else if (field === 'planned_date') {
        const time = row.plannedDevanAt ? toLocalTimeInput(row.plannedDevanAt) : '08:00'
        const iso = value ? new Date(`${value}T${time}:00`).toISOString() : null
        if (row.planId) {
          const { error } = await supabase.from('devanning_plans').update({ planned_devan_at: iso }).eq('id', row.planId)
          if (error) throw error
        }
        // Always sync schedule entry
        await supabase.from('devanning_schedule').update({ planned_devan_at: iso }).eq('id', row.scheduleId)
        if (iso) setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? { ...r, plannedDevanAt: iso, dateStr: value } : r))

      } else if (field === 'planned_time') {
        const iso = value ? new Date(`${row.dateStr}T${value}:00`).toISOString() : null
        if (row.planId) {
          const { error } = await supabase.from('devanning_plans').update({ planned_devan_at: iso }).eq('id', row.planId)
          if (error) throw error
        }
        await supabase.from('devanning_schedule').update({ planned_devan_at: iso }).eq('id', row.scheduleId)
        if (iso) setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? { ...r, plannedDevanAt: iso } : r))

      } else if (field === 'hold_note') {
        if (!row.planId) return  // only applicable when plan exists
        const { error } = await supabase.from('devanning_plans').update({ hold_reason: value || null }).eq('id', row.planId)
        if (error) throw error
        setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? { ...r, holdReason: value || null } : r))

      } else if (field === 'customs_status') {
        if (!row.planId) return  // only applicable when plan exists
        const actor = userEmail || 'unknown'
        const now = new Date().toISOString()
        const override = value || null
        const { error } = await supabase.from('devanning_plans').update({
          customs_override: override,
          customs_override_by: override ? actor : null,
          customs_override_at: override ? now : null,
        }).eq('id', row.planId)
        if (error) throw error
        setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? {
          ...r, customsOverride: override, customsOverrideBy: override ? actor : null,
          customsOverrideAt: override ? now : null, holdReason: null,
        } : r))
      }
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSavingCell(null)
    }
  }, [warehouses, userEmail])

  const checkSingleJobRef = (ref: string) => {
    if (singleDupTimerRef.current) clearTimeout(singleDupTimerRef.current)
    const trimmed = ref.trim()
    if (trimmed.length < 3) { setSingleDupCheck(null); return }
    setSingleDupCheck('checking')
    singleDupTimerRef.current = setTimeout(async () => {
      const { data } = await supabase.from('devanning_schedule').select('id, created_at').eq('job_ref', trimmed).maybeSingle()
      setSingleDupCheck(data ? { id: data.id, created_at: data.created_at } : null)
    }, 400)
  }

  const checkEditJobRef = (ref: string, currentScheduleId: string, originalRef: string) => {
    if (editDupTimerRef.current) clearTimeout(editDupTimerRef.current)
    const trimmed = ref.trim().toUpperCase()
    if (!trimmed || trimmed === originalRef.toUpperCase()) { setEditDupCheck(null); return }
    setEditDupCheck('checking')
    editDupTimerRef.current = setTimeout(async () => {
      const { data } = await supabase.from('devanning_schedule').select('id, created_at').eq('job_ref', trimmed).neq('id', currentScheduleId).maybeSingle()
      setEditDupCheck(data ? { id: data.id, created_at: data.created_at } : null)
    }, 400)
  }

  const closeEntryDialog = () => {
    setEntryDialog(false)
    setEntryMode('single')
    setSingleEntry({ planned_date: '', planned_time: '', job_ref: '', warehouse_id: '', container_no: '' })
    setMultiRows([])
    setPasteText('')
    setPasteRows([])
    if (singleDupTimerRef.current) clearTimeout(singleDupTimerRef.current)
    setSingleDupCheck(null)
  }

  const handleSaveEntries = async () => {
    setSavingEntries(true)
    try {
      type InsertRow = { job_ref: string; warehouse_id: string; container_no: string | null; planned_devan_at: string; created_by: string | null }
      let toInsert: InsertRow[] = []

      if (entryMode === 'single') {
        if (!singleEntry.job_ref.trim()) { alert('Job Ref is required.'); return }
        if (!singleEntry.warehouse_id) { alert('Warehouse is required.'); return }
        if (!singleEntry.planned_date) { alert('Date is required.'); return }
        if (singleDupCheck && singleDupCheck !== 'checking') {
          alert(`Job Ref '${singleEntry.job_ref.trim()}' already exists in the Schedule.`); return
        }
        const time = singleEntry.planned_time || '00:00'
        toInsert = [{ job_ref: singleEntry.job_ref.trim().toUpperCase(), warehouse_id: singleEntry.warehouse_id, container_no: singleEntry.container_no.toUpperCase() || null, planned_devan_at: new Date(`${singleEntry.planned_date}T${time}:00`).toISOString(), created_by: userEmail || null }]
      } else if (entryMode === 'multi') {
        const validated = multiRows.map(r => ({ ...r, error: !r.planned_date ? 'Date required' : !r.job_ref.trim() ? 'Job Ref required' : !r.warehouse_id ? 'Warehouse required' : null }))
        setMultiRows(validated)
        if (validated.some(r => r.error)) return
        const valid = validated.filter(r => r.planned_date && r.job_ref.trim() && r.warehouse_id)
        if (valid.length === 0) { alert('Add at least one row.'); return }
        const refCounts = new Map<string, number>()
        for (const r of valid) { const ref = r.job_ref.trim().toUpperCase(); refCounts.set(ref, (refCounts.get(ref) ?? 0) + 1) }
        const intraDups = new Set([...refCounts].filter(([, c]) => c > 1).map(([ref]) => ref))
        const { data: existing } = await supabase.from('devanning_schedule').select('job_ref').in('job_ref', [...refCounts.keys()])
        const dbDups = new Set((existing ?? []).map((p: any) => p.job_ref as string))
        if (intraDups.size > 0 || dbDups.size > 0) {
          setMultiRows(prev => prev.map(r => {
            if (r.error) return r
            const ref = r.job_ref.trim().toUpperCase()
            if (intraDups.has(ref)) return { ...r, error: `'${ref}' appears more than once in this batch` }
            if (dbDups.has(ref)) return { ...r, error: `'${ref}' already in Schedule` }
            return r
          }))
          return
        }
        toInsert = valid.map(r => ({ job_ref: r.job_ref.trim().toUpperCase(), warehouse_id: r.warehouse_id, container_no: r.container_no.toUpperCase() || null, planned_devan_at: new Date(`${r.planned_date}T${r.planned_time || '00:00'}:00`).toISOString(), created_by: userEmail || null }))
      } else {
        if (pasteRows.some(r => r.error)) { alert('Please fix highlighted errors before saving.'); return }
        const valid = pasteRows.filter(r => r.planned_date && r.job_ref.trim() && r.warehouse_id)
        if (valid.length === 0) { alert('No valid rows to save.'); return }
        const refCounts2 = new Map<string, number>()
        for (const r of valid) { const ref = r.job_ref.trim().toUpperCase(); refCounts2.set(ref, (refCounts2.get(ref) ?? 0) + 1) }
        const intraDups2 = new Set([...refCounts2].filter(([, c]) => c > 1).map(([ref]) => ref))
        const { data: existing2 } = await supabase.from('devanning_schedule').select('job_ref').in('job_ref', [...refCounts2.keys()])
        const dbDups2 = new Set((existing2 ?? []).map((p: any) => p.job_ref as string))
        if (intraDups2.size > 0 || dbDups2.size > 0) {
          setPasteRows(prev => prev.map(r => {
            if (r.error) return r
            const ref = r.job_ref.trim().toUpperCase()
            if (intraDups2.has(ref)) return { ...r, error: `'${ref}' in batch twice` }
            if (dbDups2.has(ref)) return { ...r, error: `'${ref}' already in Schedule` }
            return r
          }))
          return
        }
        toInsert = valid.map(r => ({ job_ref: r.job_ref.trim().toUpperCase(), warehouse_id: r.warehouse_id, container_no: r.container_no.toUpperCase() || null, planned_devan_at: new Date(`${r.planned_date}T${r.planned_time || '00:00'}:00`).toISOString(), created_by: userEmail || null }))
      }

      const { error } = await supabase.from('devanning_schedule').insert(toInsert)
      if (error) throw error
      closeEntryDialog()
      await fetchRows()
    } catch (err: any) {
      if ((err as any)?.code === '23505') {
        alert('Duplicate Job Ref — one or more job refs already exist in the Schedule.')
      } else {
        alert(err.message)
      }
    } finally {
      setSavingEntries(false)
    }
  }

  const openEdit = (row: ScheduleRow) => {
    setEditForm({
      jobRef: row.jobRef || '',
      warehouseId: row.warehouseId,
      plannedDevanAt: row.plannedDevanAt ? toLocalDateTimeInput(row.plannedDevanAt) : '',
      containerNo: row.containerNo,
      reason: '',
    })
    setActionDialog({ type: 'edit', row })
  }

  const handleSaveEdit = async () => {
    if (!actionDialog || actionDialog.type !== 'edit') return
    if (editForm.reason.trim().length < 5) return
    const row = actionDialog.row
    setActionSaving(true)
    try {
      const now = new Date().toISOString()
      const actor = userEmail || 'unknown'

      // Build schedule patch (always applied)
      const schedPatch: Record<string, any> = {
        last_reason: editForm.reason,
        last_changed_at: now,
        last_changed_by: actor,
      }
      if (editForm.warehouseId && editForm.warehouseId !== row.warehouseId) schedPatch.warehouse_id = editForm.warehouseId
      if (editForm.containerNo !== row.containerNo) schedPatch.container_no = editForm.containerNo.toUpperCase() || null
      if (editForm.plannedDevanAt) {
        const newIso = new Date(editForm.plannedDevanAt).toISOString()
        if (newIso !== row.plannedDevanAt) schedPatch.planned_devan_at = newIso
      }
      if (!row.planId && editForm.jobRef.toUpperCase() !== (row.jobRef ?? '').toUpperCase()) {
        schedPatch.job_ref = editForm.jobRef.toUpperCase() || null
      }

      // Update devanning_schedule
      const { error: schedErr } = await supabase.from('devanning_schedule').update(schedPatch).eq('id', row.scheduleId)
      if (schedErr) throw schedErr

      // If plan exists, also update devanning_plans for plan-level fields
      if (row.planId) {
        const planPatch: Record<string, any> = {
          last_reason: editForm.reason,
          last_changed_at: now,
          last_changed_by: actor,
        }
        if (schedPatch.warehouse_id) planPatch.warehouse_id = schedPatch.warehouse_id
        if (schedPatch.container_no !== undefined) planPatch.container_no = schedPatch.container_no
        if (schedPatch.planned_devan_at) planPatch.planned_devan_at = schedPatch.planned_devan_at
        const { error: planErr } = await supabase.from('devanning_plans').update(planPatch).eq('id', row.planId)
        if (planErr) throw planErr
      }

      const wh = warehouses.find(w => w.id === (schedPatch.warehouse_id ?? row.warehouseId))
      const newIso = schedPatch.planned_devan_at ?? row.plannedDevanAt
      setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? {
        ...r,
        jobRef: schedPatch.job_ref !== undefined ? schedPatch.job_ref : r.jobRef,
        warehouseId: schedPatch.warehouse_id ?? r.warehouseId,
        warehouseCode: wh?.code ?? r.warehouseCode,
        plannedDevanAt: newIso,
        dateStr: newIso?.slice(0, 10) ?? r.dateStr,
        containerNo: schedPatch.container_no !== undefined ? (schedPatch.container_no ?? '') : r.containerNo,
        lastReason: editForm.reason, lastChangedAt: now, lastChangedBy: actor,
      } : r))
      setActionDialog(null)
    } catch (err: any) {
      if ((err as any)?.code === '23505') {
        alert(`Job Ref '${editForm.jobRef.trim()}' already exists in the Schedule.`)
      } else {
        alert(err.message)
      }
    } finally {
      setActionSaving(false)
    }
  }

  const handleCancelRow = async () => {
    if (!actionDialog || actionDialog.type !== 'cancel') return
    if (actionReason.trim().length < 5) return
    const row = actionDialog.row
    setActionSaving(true)
    try {
      const now = new Date().toISOString()
      const actor = userEmail || 'unknown'
      const auditPatch = { last_reason: actionReason, last_changed_at: now, last_changed_by: actor }
      if (row.devanningId) {
        // Cancel the active devanning; keep schedule entry (status shows cancelled)
        const { error } = await supabase.from('devannings').update({ status: 'cancelled', ...auditPatch }).eq('id', row.devanningId)
        if (error) throw error
        if (row.planId) {
          await supabase.from('devanning_plans').update(auditPatch).eq('id', row.planId)
        }
        setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? {
          ...r, warehouseStatus: 'cancelled', lastReason: actionReason, lastChangedAt: now, lastChangedBy: actor,
        } : r))
      } else {
        // Pre-Devanning or plan with no devanning: remove the schedule entry entirely
        await supabase.from('devanning_schedule').delete().eq('id', row.scheduleId)
        if (row.planId) {
          await supabase.from('devanning_plan_items').delete().eq('plan_id', row.planId)
          await supabase.from('devanning_plans').delete().eq('id', row.planId)
        }
        setRows(prev => prev.filter(r => r.scheduleId !== row.scheduleId))
      }
      setActionDialog(null); setActionReason('')
    } catch (err: any) {
      alert(err.message)
    } finally {
      setActionSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    const row = deleteConfirm
    setDeleting(true)
    try {
      // Delete plan data (if a plan exists for this schedule entry)
      if (row.planId) {
        if (row.devanningId) {
          await supabase.from('devanning_items').delete().eq('devanning_id', row.devanningId)
          await supabase.from('devannings').delete().eq('id', row.devanningId)
        }
        await supabase.from('devanning_plan_items').delete().eq('plan_id', row.planId)
        await supabase.from('devanning_plans').delete().eq('id', row.planId)
      }
      // Always delete the schedule entry (#236: plan deletion must NOT cascade here)
      await supabase.from('devanning_schedule').delete().eq('id', row.scheduleId)
      setRows(prev => prev.filter(r => r.scheduleId !== row.scheduleId))
      setDeleteConfirm(null)
    } catch (err: any) {
      alert(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleHoldConfirm = async () => {
    if (!holdDialog || !holdReason.trim()) return
    const row = holdDialog
    if (!row.planId) return  // hold only applies to entries with a plan
    setHoldSaving(true)
    try {
      const now = new Date().toISOString()
      const actor = userEmail || 'unknown'
      const { error } = await supabase.from('devanning_plans').update({
        customs_override: 'HOLD',
        customs_override_by: actor,
        customs_override_at: now,
        hold_reason: holdReason,
      }).eq('id', row.planId)
      if (error) throw error
      setRows(prev => prev.map(r => r.scheduleId === row.scheduleId ? {
        ...r, customsOverride: 'HOLD', customsOverrideBy: actor, customsOverrideAt: now, holdReason,
      } : r))
      setHoldDialog(null); setHoldReason('')
    } catch (err: any) {
      alert(err.message)
    } finally {
      setHoldSaving(false)
    }
  }

  const isEditing = (rowId: string, field: EditField) => editCell?.rowId === rowId && editCell.field === field
  const isSaving = (rowId: string, field: EditField) => savingCell === `${rowId}:${field}`
  const today = toDateStr(new Date())
  const tdEdit = 'group cursor-text'

  const inputCls    = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500'
  const inputClsErr = 'w-full border border-red-500 dark:border-red-500 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-400'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-[1600px]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Devanning Schedule</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">All warehouses · grouped by week</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={whFilter} onChange={e => setWhFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Warehouses</option>
              {warehouses.map(wh => <option key={wh.id} value={wh.id}>{wh.code} — {wh.name}</option>)}
            </select>
            <ColumnToggleButton
              cols={ALL_SCHED_COL_DEFS}
              hidden={schedHidden}
              onToggle={id => toggleSchedCol(id)}
              onReset={resetSchedCols}
              onRecalculate={() => { const w = computeAutoFitWidths(ALL_SCHED_COL_DEFS); setSchedExtra({ ...schedExtra, widths: { ...w, ...schedSavedWidths } }) }}
            />
            <button onClick={() => setEntryDialog(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <span className="text-lg leading-none">+</span> New Entry
            </button>
          </div>
        </div>

        {/* No-LL pending banner */}
        {noLLCount > 0 && !noLLBannerDismissed && (
          <div className="mb-4 flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <span className="font-semibold">⚠ {noLLCount} {noLLCount === 1 ? 'entry' : 'entries'} waiting for Loading List upload.</span>
              {' '}Splits and items cannot be created until the Excel is uploaded.
            </p>
            <div className="flex items-center gap-3 flex-shrink-0">
              {!filterNoLL ? (
                <button onClick={() => setFilterNoLL(true)}
                  className="text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                  View list
                </button>
              ) : (
                <button onClick={() => setFilterNoLL(false)}
                  className="text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap">
                  Show all
                </button>
              )}
              <button onClick={() => setNoLLBannerDismissed(true)}
                className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 text-lg leading-none">
                ✕
              </button>
            </div>
          </div>
        )}
        {filterNoLL && (
          <div className="mb-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 rounded-full text-xs font-medium">Filtered: awaiting LL upload</span>
            <button onClick={() => setFilterNoLL(false)} className="text-xs text-gray-500 hover:text-gray-700 hover:underline">Clear filter</button>
          </div>
        )}

        {/* Schedule table */}
        {loading ? (
          <div className="py-20 text-center text-gray-400">Loading…</div>
        ) : loadError ? (
          <div className="py-20 text-center text-red-500 text-sm px-4">Error loading schedule: {loadError}</div>
        ) : weekGroups.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            No scheduled devannings found.
            <button onClick={() => setEntryDialog(true)} className="ml-2 text-blue-600 hover:underline">Add the first entry →</button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto" {...schedScrollProps}>
              <table ref={schedTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm">
                <DndContext sensors={schedSensors} collisionDetection={closestCenter} onDragEnd={handleSchedColDragEnd}>
                  <SortableContext items={schedColOrder} strategy={horizontalListSortingStrategy}>
                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        {visibleCols.map(col => {
                          const sortable = SORTABLE_COLS.has(col.id as SchedColKey)
                          const active   = sortable && sortCol === col.id && sortDir != null
                          return (
                            <SortableColHeader key={col.id} id={col.id}
                              style={{ width: getSchedColW(col.id), minWidth: '50px' }}
                              className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                              onClick={sortable ? () => handleColSort(col.id as SchedColKey) : undefined}
                              onResizeEnd={w => saveSchedColW(col.id, w)}
                              onResizeReset={() => resetSchedColW(col.id)}>
                              {sortable ? (
                                <span className="flex items-center gap-0.5 cursor-pointer select-none">
                                  {col.label}
                                  <span className={`ml-0.5 text-[10px] leading-none transition-opacity ${active ? 'text-blue-500 opacity-100' : 'text-gray-300 opacity-0 group-hover/col:opacity-60'}`}>
                                    {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                                  </span>
                                </span>
                              ) : col.label}
                            </SortableColHeader>
                          )
                        })}
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                  </SortableContext>
                </DndContext>
                <tbody>
                  {weekGroups.map(group => {
                    const collapsed = collapsedWeeks.has(group.weekKey)
                    const hasToday = group.rows.some(r => r.dateStr === today)
                    return (
                      <>
                        {/* Week header */}
                        <tr key={`wh-${group.weekKey}`}
                          className="bg-gray-100 dark:bg-gray-700/80 border-y border-gray-200 dark:border-gray-600 cursor-pointer select-none"
                          onClick={() => toggleWeek(group.weekKey)}>
                          <td colSpan={visibleCols.length + 1} className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-gray-400 text-xs font-mono transition-transform ${collapsed ? '' : 'rotate-90'}`}>▶</span>
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{group.weekLabel}</span>
                              {hasToday && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded-full font-semibold ml-1">This week</span>}
                              <span className="text-xs text-gray-400 ml-auto">{group.rows.length} entr{group.rows.length !== 1 ? 'ies' : 'y'}</span>
                            </div>
                          </td>
                        </tr>

                        {!collapsed && group.rows.map(row => {
                          const isToday_ = row.dateStr === today
                          const isOverdue = !!(row.freeDate && row.dateStr > row.freeDate && row.warehouseStatus !== 'devanning_completed' && row.warehouseStatus !== 'cancelled')
                          const isCancelled = row.warehouseStatus === 'cancelled'
                          const noLL = !row.sourceLoadingListId && !!row.jobRef
                          const rowId = row.scheduleId
                          const effectiveCustoms = row.customsOverride ?? row.customsStatus
                          const daysPastFree = row.freeDate ? daysBetween(row.freeDate, row.dateStr) : null
                          const rowCls = isCancelled
                            ? 'opacity-60 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                            : isOverdue
                              ? OVERDUE_ROW_CLASS.danger
                              : noLL
                                ? 'bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                                : isToday_
                                  ? 'bg-blue-50/40 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'

                          const cellMap: Record<string, React.ReactNode> = {
                            date: (
                              <td key="date" className={`px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap ${tdEdit}`}
                                onClick={() => !isEditing(rowId, 'planned_date') && !isSaving(rowId, 'planned_date') && setEditCell({ rowId, field: 'planned_date', value: row.dateStr })}>
                                {isSaving(rowId, 'planned_date') ? <Spin /> : isEditing(rowId, 'planned_date') ? (
                                  <InlineDate value={editCell!.value} onCommit={v => saveCell(row, 'planned_date', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1">
                                    {isToday_ && <span className="text-[10px] px-1 py-0.5 bg-blue-500 text-white rounded font-bold mr-0.5">Today</span>}
                                    {fmtShortDate(row.dateStr)}
                                    {editIcon}
                                  </span>
                                )}
                              </td>
                            ),
                            job_ref: (
                              <td key="job_ref" className="px-3 py-2.5 font-mono text-blue-700 dark:text-blue-400 text-xs whitespace-nowrap">
                                <span className="inline-flex items-center gap-1">
                                  {row.jobRef ? <span>{row.jobRef}</span> : '—'}
                                  {noLL && <span title="Loading List not uploaded. Click ACTIONS → Upload LL to enable Smart Devan Split" className="text-amber-500 cursor-default">⚠</span>}
                                  {row.lastReason && (
                                    <InfoIcon title={`${row.lastReason}${row.lastChangedBy ? ` — ${row.lastChangedBy}` : ''}${row.lastChangedAt ? ` · ${new Date(row.lastChangedAt).toLocaleString('en-GB')}` : ''}`} />
                                  )}
                                </span>
                              </td>
                            ),
                            customer: (
                              <td key="customer" className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {row.customerName || <span className="text-gray-300 dark:text-gray-600">—</span>}
                              </td>
                            ),
                            container: (
                              <td key="container" className={`px-3 py-2.5 font-mono font-semibold text-gray-800 dark:text-gray-100 ${tdEdit}`}
                                onClick={() => !isEditing(rowId, 'container_no') && !isSaving(rowId, 'container_no') && setEditCell({ rowId, field: 'container_no', value: row.containerNo })}>
                                {isSaving(rowId, 'container_no') ? <Spin /> : isEditing(rowId, 'container_no') ? (
                                  <InlineText value={editCell!.value} onCommit={v => saveCell(row, 'container_no', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1 text-xs">{row.containerNo || '—'}{editIcon}</span>
                                )}
                              </td>
                            ),
                            warehouse: (
                              <td key="warehouse" className={`px-3 py-2.5 ${tdEdit}`}
                                onClick={() => !isEditing(rowId, 'warehouse_id') && !isSaving(rowId, 'warehouse_id') && setEditCell({ rowId, field: 'warehouse_id', value: row.warehouseId })}>
                                {isSaving(rowId, 'warehouse_id') ? <Spin /> : isEditing(rowId, 'warehouse_id') ? (
                                  <InlineSelect<string> value={editCell!.value}
                                    options={warehouses.map(w => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
                                    onCommit={v => saveCell(row, 'warehouse_id', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1 text-gray-700 dark:text-gray-200 text-xs font-medium">{row.warehouseCode}{editIcon}</span>
                                )}
                              </td>
                            ),
                            eta: (
                              <td key="eta" className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {row.eta ? fmtShortDate(row.eta.slice(0, 10)) : '—'}
                              </td>
                            ),
                            free_days: (
                              <td key="free_days" className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center">
                                {row.freeDays ?? '—'}
                              </td>
                            ),
                            free_date: (
                              <td key="free_date" className={`px-3 py-2.5 text-xs whitespace-nowrap ${isOverdue ? OVERDUE_TEXT_CLASS.danger : 'text-gray-500 dark:text-gray-400'}`}>
                                {fmtShortDate(row.freeDate)}
                                {isOverdue && <span className="ml-1" title="Planned devan date exceeds free demurrage period">{overdueBadgeText(daysPastFree!, 'danger')}</span>}
                              </td>
                            ),
                            time: (
                              <td key="time" className={`px-3 py-2.5 font-mono text-xs text-gray-600 dark:text-gray-300 ${tdEdit}`}
                                onClick={() => !isEditing(rowId, 'planned_time') && !isSaving(rowId, 'planned_time') && setEditCell({ rowId, field: 'planned_time', value: toLocalTimeInput(row.plannedDevanAt) })}>
                                {isSaving(rowId, 'planned_time') ? <Spin /> : isEditing(rowId, 'planned_time') ? (
                                  <InlineTime value={editCell!.value} onCommit={v => saveCell(row, 'planned_time', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1">
                                    {toLocalTimeInput(row.plannedDevanAt) === '00:00' ? '—' : new Date(row.plannedDevanAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                    {editIcon}
                                  </span>
                                )}
                              </td>
                            ),
                            wh_status: (
                              <td key="wh_status" className="px-3 py-2.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${WH_STATUS_COLORS[row.warehouseStatus] ?? 'bg-gray-100 text-gray-500'}`}>
                                  {WH_STATUS_LABELS[row.warehouseStatus] ?? row.warehouseStatus}
                                </span>
                              </td>
                            ),
                            customs: (
                              <td key="customs" className={`px-3 py-2.5 ${tdEdit}`}
                                onClick={() => !isEditing(rowId, 'customs_status') && !isSaving(rowId, 'customs_status') && setEditCell({ rowId, field: 'customs_status', value: row.customsOverride ?? '' })}>
                                {isSaving(rowId, 'customs_status') ? <Spin /> : isEditing(rowId, 'customs_status') ? (
                                  <InlineCustoms value={editCell!.value}
                                    onCommit={v => saveCell(row, 'customs_status', v)}
                                    onCancel={() => setEditCell(null)}
                                    onRequestHold={() => { setEditCell(null); setHoldDialog(row); setHoldReason(row.holdReason || '') }} />
                                ) : effectiveCustoms ? (
                                  <span className="flex items-center gap-0.5">
                                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${CUSTOMS_COLORS[effectiveCustoms] ?? 'bg-gray-100 text-gray-600'}`}>
                                      {effectiveCustoms}
                                    </span>
                                    {row.customsOverride && (
                                      <InfoIcon title={`Manually overridden by ${row.customsOverrideBy ?? '?'}${row.customsOverrideAt ? ` · ${new Date(row.customsOverrideAt).toLocaleString('en-GB')}` : ''}`} />
                                    )}
                                    {editIcon}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-gray-400 text-xs">—{editIcon}</span>
                                )}
                              </td>
                            ),
                            hold_note: (
                              <td key="hold_note" className={`group px-3 py-2.5 text-xs max-w-[160px] ${tdEdit}`}
                                onClick={() => !isEditing(rowId, 'hold_note') && !isSaving(rowId, 'hold_note') && setEditCell({ rowId, field: 'hold_note', value: row.holdReason ?? '' })}>
                                {isSaving(rowId, 'hold_note') ? <Spin /> : isEditing(rowId, 'hold_note') ? (
                                  <InlineNote value={editCell!.value} onCommit={v => saveCell(row, 'hold_note', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className={`flex items-center gap-1 ${row.holdReason ? 'text-amber-700 dark:text-amber-400' : 'text-gray-300'}`}>
                                    <span className="truncate">{row.holdReason || '—'}</span>
                                    {editIcon}
                                  </span>
                                )}
                              </td>
                            ),
                            created: (
                              <td key="created" className="px-3 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                                {fmtCreated(row.createdAt, row.createdBy)}
                              </td>
                            ),
                          }

                          return (
                            <tr key={row.scheduleId} className={`${rowCls} border-b border-gray-100 dark:border-gray-700/50`}>
                              {visibleCols.map(col => cellMap[col.id])}
                              {/* ACTIONS always shown */}
                              <td className="px-3 py-2.5 text-xs whitespace-nowrap space-x-2">
                                {noLL ? (
                                  <Link
                                    to={`/warehouse/${row.warehouseId}/loading-lists/upload?job_ref=${encodeURIComponent(row.jobRef ?? '')}&container_no=${encodeURIComponent(row.containerNo)}`}
                                    className="text-amber-600 hover:underline font-medium">
                                    Upload LL
                                  </Link>
                                ) : row.devanningId ? (
                                  <Link to={`/warehouse/${row.warehouseId}/devannings/${row.devanningId}`} className="text-blue-600 hover:underline font-medium">Open →</Link>
                                ) : row.planId ? (
                                  <Link to={`/warehouse/${row.warehouseId}/plans/${row.planId}`} className="text-gray-500 hover:underline">Plan →</Link>
                                ) : null}
                                {!isCancelled && (
                                  <>
                                    <button onClick={() => openEdit(row)} className="text-gray-600 hover:underline">Edit</button>
                                    <button onClick={() => { setActionDialog({ type: 'cancel', row }); setActionReason('') }}
                                      className="text-red-500 hover:underline">Cancel</button>
                                  </>
                                )}
                                <button onClick={() => setDeleteConfirm(row)} className="text-red-400 hover:text-red-600 hover:underline">Delete</button>
                              </td>
                            </tr>
                          )
                        })}
                      </>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <StickyHScrollbar {...schedScrollBar} />

      {/* Edit dialog */}
      {actionDialog?.type === 'edit' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Edit Schedule Entry</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Job Ref{actionDialog.row.planId ? ' (from Loading List)' : ' *'}
                </label>
                <div className="relative">
                  <input
                    className={editDupCheck && editDupCheck !== 'checking' ? inputClsErr : inputCls}
                    value={editForm.jobRef} maxLength={10}
                    disabled={!!actionDialog.row.planId}
                    onChange={e => {
                      const v = e.target.value.toUpperCase()
                      setEditForm(p => ({ ...p, jobRef: v }))
                      if (!actionDialog.row.planId)
                        checkEditJobRef(v, actionDialog.row.scheduleId, actionDialog.row.jobRef ?? '')
                    }}
                    onBlur={() => {
                      if (!actionDialog.row.sourceLoadingListId)
                        checkEditJobRef(editForm.jobRef ?? '', actionDialog.row.planId ?? '', actionDialog.row.jobRef ?? '')
                    }}
                  />
                  {editDupCheck === 'checking' && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">checking…</span>
                  )}
                </div>
                {editDupCheck && editDupCheck !== 'checking' && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    ⚠ &lsquo;{editForm.jobRef}&rsquo; already in Schedule.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Warehouse *</label>
                <select value={editForm.warehouseId} onChange={e => setEditForm(p => ({ ...p, warehouseId: e.target.value }))} className={inputCls}>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date & Time *</label>
                <input type="datetime-local" className={inputCls} value={editForm.plannedDevanAt}
                  onChange={e => setEditForm(p => ({ ...p, plannedDevanAt: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Container No</label>
                <input className={inputCls} value={editForm.containerNo}
                  onChange={e => setEditForm(p => ({ ...p, containerNo: e.target.value.toUpperCase() }))} />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Reason * <span className="text-gray-400 font-normal">(min 5 chars)</span>
              </label>
              <textarea rows={2} className={`${inputCls} resize-none`} placeholder="Reason for this change…"
                value={editForm.reason} onChange={e => setEditForm(p => ({ ...p, reason: e.target.value }))} />
              {editForm.reason.length > 0 && editForm.reason.trim().length < 5 && (
                <p className="mt-0.5 text-xs text-red-500">At least 5 characters required.</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setActionDialog(null)}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button disabled={actionSaving || editForm.reason.trim().length < 5 || (editDupCheck !== null && editDupCheck !== 'checking')} onClick={handleSaveEdit}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {actionSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel dialog */}
      {actionDialog?.type === 'cancel' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-0.5">Cancel Schedule Entry</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 font-mono">{actionDialog.row.jobRef || actionDialog.row.containerNo || '—'}</p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Reason * <span className="text-gray-400 font-normal">(min 5 chars)</span>
              </label>
              <textarea rows={3} className={`${inputCls} resize-none`} placeholder="Reason for cancelling…"
                value={actionReason} onChange={e => setActionReason(e.target.value)} />
              {actionReason.length > 0 && actionReason.trim().length < 5 && (
                <p className="mt-0.5 text-xs text-red-500">At least 5 characters required.</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setActionDialog(null); setActionReason('') }}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Back
              </button>
              <button disabled={actionSaving || actionReason.trim().length < 5} onClick={handleCancelRow}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {actionSaving ? 'Saving…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Delete Entry</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
              Permanently delete <strong className="font-mono">{deleteConfirm.jobRef || deleteConfirm.containerNo || '—'}</strong>?
            </p>
            <p className="text-xs text-red-600 mb-4">This cannot be undone. All associated devanning data will be deleted.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New entry dialog */}
      {entryDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl mx-auto my-8">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">New Manual Entry</h3>
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 gap-0.5">
                  {(['single', 'multi', 'paste'] as EntryMode[]).map(m => (
                    <button key={m} onClick={() => setEntryMode(m)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${entryMode === m ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
                      {m === 'single' ? 'Single' : m === 'multi' ? 'Multiple' : 'Paste'}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={closeEntryDialog} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">✕</button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {entryMode === 'single' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date *</label>
                    <input type="date" className={inputCls} value={singleEntry.planned_date}
                      onChange={e => setSingleEntry(p => ({ ...p, planned_date: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Time</label>
                    <input type="time" className={inputCls} value={singleEntry.planned_time}
                      onChange={e => setSingleEntry(p => ({ ...p, planned_time: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Job Ref *</label>
                    <div className="relative">
                      <input
                        className={singleDupCheck && singleDupCheck !== 'checking' ? inputClsErr : inputCls}
                        placeholder="e.g. KI17875" maxLength={10}
                        value={singleEntry.job_ref}
                        onChange={e => {
                          const v = e.target.value.toUpperCase()
                          setSingleEntry(p => ({ ...p, job_ref: v }))
                          checkSingleJobRef(v)
                        }}
                        onBlur={() => checkSingleJobRef(singleEntry.job_ref)}
                      />
                      {singleDupCheck === 'checking' && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">checking…</span>
                      )}
                    </div>
                    {singleDupCheck && singleDupCheck !== 'checking' && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        ⚠ &lsquo;{singleEntry.job_ref}&rsquo; already in Schedule{singleDupCheck.created_at ? ` (added ${new Date(singleDupCheck.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})` : ''}.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Warehouse *</label>
                    <select value={singleEntry.warehouse_id}
                      onChange={e => setSingleEntry(p => ({ ...p, warehouse_id: e.target.value }))}
                      className={inputCls}>
                      <option value="">Select…</option>
                      {warehouses.map(wh => <option key={wh.id} value={wh.id}>{wh.code} — {wh.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Container No</label>
                    <input className={inputCls} placeholder="ABCU1234567"
                      value={singleEntry.container_no}
                      onChange={e => setSingleEntry(p => ({ ...p, container_no: e.target.value.toUpperCase() }))} />
                  </div>
                </div>
              )}

              {entryMode === 'multi' && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          {['Date *', 'Time', 'Job Ref *', 'Warehouse *', 'Container No', ''].map(h => (
                            <th key={h} className="pb-2 pr-2 text-left font-medium text-gray-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {multiRows.length === 0 && (
                          <tr><td colSpan={6} className="py-4 text-center text-gray-400 text-xs">No rows yet — click "+ Add Row" below</td></tr>
                        )}
                        {multiRows.map((row, idx) => (
                          <tr key={row._id} className={row.error ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                            <td className="py-1 pr-2 min-w-[120px]">
                              <input type="date" className={`${inputCls} text-xs py-1`} value={row.planned_date}
                                onChange={e => setMultiRows(prev => prev.map((r, i) => i === idx ? { ...r, planned_date: e.target.value, error: null } : r))} />
                            </td>
                            <td className="py-1 pr-2 w-24">
                              <input type="time" className={`${inputCls} text-xs py-1`} value={row.planned_time}
                                onChange={e => setMultiRows(prev => prev.map((r, i) => i === idx ? { ...r, planned_time: e.target.value } : r))} />
                            </td>
                            <td className="py-1 pr-2 w-28">
                              <input className={`${inputCls} text-xs py-1`} placeholder="KI17875" maxLength={10} value={row.job_ref}
                                onChange={e => setMultiRows(prev => prev.map((r, i) => i === idx ? { ...r, job_ref: e.target.value.toUpperCase(), error: null } : r))} />
                            </td>
                            <td className="py-1 pr-2">
                              <select className={`${inputCls} text-xs py-1`} value={row.warehouse_id}
                                onChange={e => {
                                  const wh = warehouses.find(w => w.id === e.target.value)
                                  setMultiRows(prev => prev.map((r, i) => i === idx ? { ...r, warehouse_id: e.target.value, warehouse_code: wh?.code ?? '', error: null } : r))
                                }}>
                                <option value="">Select…</option>
                                {warehouses.map(wh => <option key={wh.id} value={wh.id}>{wh.code}</option>)}
                              </select>
                            </td>
                            <td className="py-1 pr-2 w-32">
                              <input className={`${inputCls} text-xs py-1`} placeholder="ABCU1234567" value={row.container_no}
                                onChange={e => setMultiRows(prev => prev.map((r, i) => i === idx ? { ...r, container_no: e.target.value.toUpperCase() } : r))} />
                            </td>
                            <td className="py-1 text-center">
                              <button onClick={() => setMultiRows(prev => prev.filter((_, i) => i !== idx))}
                                className="text-red-400 hover:text-red-600 font-bold text-base leading-none px-1">✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {multiRows.some(r => r.error) && (
                    <div className="mt-2 space-y-0.5">
                      {multiRows.map((r, i) => r.error ? <p key={r._id} className="text-xs text-red-500">Row {i + 1}: {r.error}</p> : null)}
                    </div>
                  )}
                  <button onClick={() => setMultiRows(prev => [...prev, newBlankRow()])}
                    className="mt-3 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                    <span className="text-base leading-none">+</span> Add Row
                  </button>
                </div>
              )}

              {entryMode === 'paste' && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Paste tab-separated rows from Excel. Columns: <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">Date · Time · Job Ref · Warehouse Code · Container No</span>
                    {' '}(Time and Container No optional)
                  </p>
                  <textarea rows={6} className={`${inputCls} font-mono text-xs resize-y`}
                    placeholder={"2026-07-01\t09:00\tKI17875\tGWH\tABCU1234567\n2026-07-02\t\tKI17876\tGWH\t"}
                    value={pasteText}
                    onChange={e => { setPasteText(e.target.value); setPasteRows(parsePasteRows(e.target.value, warehouses)) }} />
                  {pasteRows.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-1">
                        {pasteRows.length} row{pasteRows.length !== 1 ? 's' : ''} parsed · <span className={pasteRows.some(r => r.error) ? 'text-red-500' : 'text-green-600'}>{pasteRows.filter(r => !r.error).length} valid</span>
                      </p>
                      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                              {['Date', 'Time', 'Job Ref', 'Warehouse', 'Container No', 'Status'].map(h => (
                                <th key={h} className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase text-[10px] tracking-wide">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {pasteRows.map(r => (
                              <tr key={r._id} className={r.error ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                                <td className="px-2 py-1.5 font-mono">{r.planned_date || '—'}</td>
                                <td className="px-2 py-1.5 font-mono">{r.planned_time || '—'}</td>
                                <td className="px-2 py-1.5 font-mono font-semibold">{r.job_ref || '—'}</td>
                                <td className="px-2 py-1.5">{r.warehouse_code || '—'}</td>
                                <td className="px-2 py-1.5 font-mono">{r.container_no || '—'}</td>
                                <td className="px-2 py-1.5">
                                  {r.error ? <span className="text-red-600 font-medium">{r.error}</span> : <span className="text-green-600 font-medium">✓ OK</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-400">
                {entryMode === 'multi' && `${multiRows.length} row${multiRows.length !== 1 ? 's' : ''}`}
                {entryMode === 'paste' && pasteRows.length > 0 && `${pasteRows.filter(r => !r.error).length} / ${pasteRows.length} valid`}
              </div>
              <div className="flex gap-3">
                <button onClick={closeEntryDialog}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                  Cancel
                </button>
                <button
                  disabled={savingEntries
                    || (entryMode === 'single' && singleDupCheck !== null && singleDupCheck !== 'checking')
                    || (entryMode === 'multi' && multiRows.length === 0)
                    || (entryMode === 'paste' && pasteRows.filter(r => !r.error).length === 0)}
                  onClick={handleSaveEntries}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {savingEntries ? 'Saving…' : entryMode === 'single' ? 'Save Entry' : `Save All (${entryMode === 'multi' ? multiRows.length : pasteRows.filter(r => !r.error).length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HOLD reason dialog */}
      {holdDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-0.5">Set Customs HOLD</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 font-mono">{holdDialog.jobRef || holdDialog.containerNo || '—'}</p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Hold Reason *</label>
              <textarea rows={3} className={`${inputCls} resize-none`} placeholder="e.g. Pending duty payment, awaiting HMRC clearance…"
                value={holdReason} onChange={e => setHoldReason(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setHoldDialog(null); setHoldReason('') }}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button disabled={holdSaving || !holdReason.trim()} onClick={handleHoldConfirm}
                className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {holdSaving ? 'Saving…' : 'Set HOLD'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
